import { useQuery, useInfiniteQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { monitoringDetectedApi, monitoringFilterApi } from '~/apis';
import {
  MarqCommonEnumsPotentialAuthorizedReasonEnum,
  MarqCommonEnumsReportableReasonEnum,
  MonitoringCommonViewProtocolsGetAllListingIdRequest,
  MonitoringFilterProtocolPagingRequest,
  MonitoringFilterProtocolRequest,
  MonitoringFilterProtocolResponse,
} from '~/swagger/data-contracts';
import { parseDropDown } from '~/utils';
import { reviewNeededApi } from '~/apis/enforcement/reviewNeeded';
import { DropDown } from '~/components/Filters/@types';
import { QUERY_KEY_FILTER } from '~/apis/@constants';

export const QueryKeyEnforcement = 'enforcement';

type AdditionalQueryOptions = {
  staleTime?: number;
  enabled?: boolean;
};

export const useGetAllDomains = <T = null>(
  request: MonitoringFilterProtocolRequest & T,
  options?: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, DropDown>
) => {
  return useQuery({
    queryKey: [QUERY_KEY_FILTER, 'get-all-domains', request],
    queryFn: async () => {
      const { data } = await monitoringFilterApi.filterGetAllDomainsCreate(request);
      return data;
    },
    ...options,
    select: parseDropDown,
    enabled: !!request.channel_type && options?.enabled,
  });
};

export const useGetAllAssets = (request: MonitoringFilterProtocolRequest, options?: AdditionalQueryOptions) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-assets', request],
    async () => {
      const { data } = await monitoringFilterApi.filterGetAllAssetsCreate(request);
      return data;
    },
    { ...options, select: parseDropDown, enabled: !!request.channel_type }
  );
};

export const useGetAllSellers = (request: MonitoringFilterProtocolPagingRequest, options?: AdditionalQueryOptions) => {
  const size = request.size || 0;
  return useInfiniteQuery(
    [QUERY_KEY_FILTER, 'get-all-sellers', request],
    async ({ pageParam }) => {
      const { data } = await monitoringFilterApi.filterGetAllSellersCreate({
        ...request,
        search_after: pageParam || [],
      });
      return data;
    },
    {
      ...options,
      select: (data) => {
        return {
          pageParams: data.pageParams,
          pages: data.pages.map((page) => parseDropDown<string>(page)),
        };
      },
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.search_after && lastPage.search_after.length > 0) {
          return lastPage.search_after;
        }
        return false;
      },
      enabled: !!request.channel_type, // MonitoringFilterProtocolRequest, MonitoringFilterProtocolPagingRequest 둘다 channel_type 이 있기에 최소한의 방어로직 추가.
    }
  );
};

export const useGetAllModels = <T = null>(
  request: MonitoringFilterProtocolRequest & T,
  options?: AdditionalQueryOptions
) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-models', request],
    async () => {
      const { data } = await monitoringFilterApi.filterGetAllModelCreate(request);
      return data;
    },
    {
      ...options,
      select: parseDropDown,
    }
  );
};

export const useGetAllReasons = <T = null>(
  request: MonitoringFilterProtocolRequest & T,
  options?: AdditionalQueryOptions
) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-reasons', request],
    async () => {
      const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedGetAllPotentialAuthorizedReasonsCreate(
        request
      );
      return data;
    },
    {
      ...options,
      select: parseDropDown<MarqCommonEnumsPotentialAuthorizedReasonEnum>,
    }
  );
};

export const useGetAllPtPlatforms = <T = null>(
  request: MonitoringFilterProtocolRequest & T,
  options?: AdditionalQueryOptions
) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-platforms', request],
    async () => {
      const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedGetAllDomainsCreate(request);
      return data;
    },
    {
      ...options,
      select: parseDropDown,
    }
  );
};

export const useGetAllPtSellers = <T = null>(
  request: MonitoringFilterProtocolPagingRequest,
  options?: AdditionalQueryOptions
) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-sellers', request],
    async () => {
      const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedGetAllSellersCreate(request);
      return data;
    },
    {
      ...options,
      select: parseDropDown<string>,
    }
  );
};

export const useGetAllReportable = (request: MonitoringFilterProtocolRequest, options?: AdditionalQueryOptions) => {
  return useQuery(
    [QUERY_KEY_FILTER, 'get-all-reportable', request],
    async () => {
      const { data } = await monitoringFilterApi.filterGetAllReportableCreate(request);
      return data;
    },
    { ...options, select: parseDropDown<MarqCommonEnumsReportableReasonEnum>, enabled: !!request.channel_type }
  );
};

export const useGetAllRules = (request: MonitoringFilterProtocolRequest, options?: AdditionalQueryOptions) =>
  useQuery(
    [QUERY_KEY_FILTER, 'get-all-rules', request],
    async () => {
      const { data } = await monitoringFilterApi.filterEnforcementGetAllRulesCreate(request);
      return data;
    },
    { ...options, select: parseDropDown, enabled: !!request.channel_type }
  );

export const useExcelExportMutation = () => useMutation(monitoringDetectedApi.excelExportCreate);

export const useListingIdExcelExportMutation = () => useMutation(monitoringDetectedApi.excelExportListingIdCreate);

export const useReviewNeededListingIdExcelExportMutation = () =>
  useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedExcelExportSellerIdCreate);

export const useReviewNeededExcelExportMutation = () =>
  useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedExcelExportCreate);
